import { useSearchParams } from 'react-router';
import { isCupedV2EligibleAndEnabled } from '@gonfalon/experiments/isCupedV2Eligible';

import { useExperimentIterationContext } from '../providers/ExperimentIterationContextProvider';

export const useAnalysisAppendParam = (
  multipleComparisonCorrectionMethod?: string,
): {
  mccStatus: boolean;
  mccAnalysisQueryParam: 'disabled' | undefined;
  sequentialTestingStatus: boolean;
  sequentialTestingAnalysisQueryParam: 'disabled' | undefined;
  cupedStatus: boolean;
  cupedAnalysisQueryParam: 'disabled' | undefined;
  experimentHasCupedV2: boolean;
  handleAppendAnalysisParam: ({
    mccEnabled,
    sequentialTestingEnabled,
    cupedEnabled,
  }: {
    mccEnabled?: boolean;
    sequentialTestingEnabled?: boolean;
    cupedEnabled?: boolean;
  }) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { experiment, iteration } = useExperimentIterationContext();

  const experimentHasMCC = !!multipleComparisonCorrectionMethod;
  const mccStatus = experimentHasMCC && !searchParams.has('mcc');

  const experimentHasSequentialTesting =
    experiment.methodology === 'frequentist' && !!experiment.analysisConfig?.sequentialTestingEnabled;
  const sequentialTestingStatus = experimentHasSequentialTesting && !searchParams.has('sequentialTesting');

  const experimentHasCupedV2 = isCupedV2EligibleAndEnabled(experiment.dataSource, iteration.cupedEnabled);
  const cupedStatus = experimentHasCupedV2 && !searchParams.has('cuped');

  const handleAppendAnalysisParam = ({
    mccEnabled,
    sequentialTestingEnabled,
    cupedEnabled,
  }: {
    mccEnabled?: boolean;
    sequentialTestingEnabled?: boolean;
    cupedEnabled?: boolean;
  }) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);

        if (mccEnabled !== undefined) {
          if (!mccEnabled && experimentHasMCC) {
            newParams.set('mcc', 'disabled');
          } else if (mccEnabled && experimentHasMCC) {
            newParams.delete('mcc');
          }
        }

        if (sequentialTestingEnabled !== undefined) {
          if (!sequentialTestingEnabled && experimentHasSequentialTesting) {
            newParams.set('sequentialTesting', 'disabled');
          } else if (sequentialTestingEnabled && experimentHasSequentialTesting) {
            newParams.delete('sequentialTesting');
          }
        }

        if (cupedEnabled !== undefined) {
          if (!cupedEnabled && experimentHasCupedV2) {
            newParams.set('cuped', 'disabled');
          } else if (cupedEnabled && experimentHasCupedV2) {
            newParams.delete('cuped');
          }
        }

        return newParams;
      },
      { preventScrollReset: true },
    );
  };

  const mccAnalysisQueryParam = experimentHasMCC ? (mccStatus ? undefined : 'disabled') : undefined;
  const sequentialTestingAnalysisQueryParam = experimentHasSequentialTesting
    ? sequentialTestingStatus
      ? undefined
      : 'disabled'
    : undefined;
  const cupedAnalysisQueryParam = experimentHasCupedV2 ? (cupedStatus ? undefined : 'disabled') : undefined;

  return {
    mccStatus,
    mccAnalysisQueryParam,
    sequentialTestingStatus,
    sequentialTestingAnalysisQueryParam,
    cupedStatus,
    cupedAnalysisQueryParam,
    experimentHasCupedV2,
    handleAppendAnalysisParam,
  };
};
